import { useState } from 'react';
import { motion } from 'framer-motion';
import { Bell, Search, User, LogOut, Command, GraduationCap, Sun, Moon, Laptop } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/useAuth';
import { useNotifications } from '@/hooks/useNotifications';
import { useOnboarding } from '@/contexts/OnboardingContext';
import { NotificationPanel } from '@/components/notifications/NotificationPanel';
import { GlobalCommandPalette } from '@/components/search/GlobalCommandPalette';
import { useNavigate } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface HeaderProps {
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
}

export const Header = ({
  searchValue = '',
  onSearchChange,
  searchPlaceholder = "Search campaigns, leads, or call logs..."
}: HeaderProps) => {
  const { user, signOut } = useAuth();
  const { unreadCount } = useNotifications();
  const { resetOnboarding } = useOnboarding();
  const navigate = useNavigate();
  const { setTheme } = useTheme();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);

  const handleSignOut = async () => {
    await signOut();
  };

  const handleStartTutorial = () => {
    resetOnboarding();
    navigate('/dashboard');
  };

  return (
    <motion.header
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="sticky top-0 z-50 border-b border-card-border bg-card backdrop-blur-sm shadow-sm"
    >
      <div className="flex h-16 items-center px-6">
        <div className="flex items-center space-x-4">
          <SidebarTrigger />
          {/* Global Search */}
          <div className="flex-1 max-w-md lg:max-w-2xl">
            <Button
              variant="outline"
              className="w-full justify-start text-muted-foreground bg-accent/50 border-none h-10 lg:h-12 hover:bg-accent/70"
              onClick={() => setShowCommandPalette(true)}
            >
              <Search className="mr-2 h-4 w-4 bg-transparent" />
              <span className="flex-1 text-left">Search everything...</span>
              <kbd className="pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100">
                <Command className="h-3 w-3 bg-transparent" />K
              </kbd>
            </Button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center space-x-4 ml-auto">
          {/* Notifications */}
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            onClick={() => setShowNotifications(!showNotifications)}
          >
            <Bell className="h-5 w-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 h-5 w-5 bg-error text-error-foreground rounded-full text-xs flex items-center justify-center font-medium min-w-[1.25rem]">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </Button>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <User className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-card border border-card-border shadow-lg z-50">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{user?.email}</p>
                  <p className="text-xs text-muted-foreground">My Account</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem>
                <a href="/profile" className="w-full">Profile</a>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <a href="/billing" className="w-full">Billing</a>
              </DropdownMenuItem>
              <DropdownMenuItem>
                <a href="/settings" className="w-full">Settings</a>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Theme</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => setTheme("light")}>
                <Sun className="mr-2 h-4 w-4" />
                Light
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme("dark")}>
                <Moon className="mr-2 h-4 w-4" />
                Dark
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setTheme("system")}>
                <Laptop className="mr-2 h-4 w-4" />
                System
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleStartTutorial}>
                <GraduationCap className="mr-2 h-4 w-4" />
                Tutorial
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Notification Panel */}
      <NotificationPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />

      {/* Global Command Palette */}
      <GlobalCommandPalette
        open={showCommandPalette}
        onOpenChange={setShowCommandPalette}
      />
    </motion.header>
  );
};